import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';

type Lesson = {
  id: string;
  slug: string;
  title: string;
  body: string;
  estMinutes: number;
  moduleId: string;
  moduleTitle: string;
  weekNumber: number;
};

export default function LessonPage() {
  const { slug } = useParams();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ kind: 'locked' | 'not_found' | 'other'; unlockAt?: string | null } | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setLesson(null);
    void (async () => {
      try {
        const res = await api.get<{ lesson: Lesson; completedAt: string | null }>(
          `/api/curriculum/lesson/${slug}`
        );
        setLesson(res.lesson);
        setCompletedAt(res.completedAt);
      } catch (err) {
        const code = (err as { error?: string; unlockAt?: string | null })?.error;
        if (code === 'locked') {
          setError({ kind: 'locked', unlockAt: (err as { unlockAt?: string }).unlockAt });
        } else if (code === 'not_found') {
          setError({ kind: 'not_found' });
        } else {
          setError({ kind: 'other' });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  async function markComplete() {
    if (!slug) return;
    const res = await api.post<{ ok: true; completedAt: string }>(
      `/api/curriculum/lesson/${slug}/complete`
    );
    setCompletedAt(res.completedAt);
  }

  if (loading) return <div className="py-12 text-muted">Loading…</div>;

  if (error) {
    return (
      <div className="mx-auto max-w-2xl py-12">
        {error.kind === 'locked' && (
          <>
            <h1 className="text-2xl font-semibold">This week is still locked.</h1>
            <p className="mt-2 text-muted">
              {error.unlockAt
                ? `It unlocks on ${new Date(error.unlockAt).toLocaleDateString()}.`
                : 'Your enrollment cadence has not reached this week yet.'}
            </p>
          </>
        )}
        {error.kind === 'not_found' && <h1 className="text-2xl font-semibold">Lesson not found.</h1>}
        {error.kind === 'other' && <h1 className="text-2xl font-semibold">Something went wrong.</h1>}
        <Link to="/dashboard" className="mt-6 inline-block text-sm text-amber hover:underline">
          ← Back to dashboard
        </Link>
      </div>
    );
  }

  if (!lesson) return null;

  return (
    <article className="mx-auto max-w-2xl py-6">
      <Link to="/dashboard" className="text-xs text-muted hover:text-text">
        ← Dashboard
      </Link>
      <p className="mt-6 font-mono text-xs uppercase tracking-widest text-amber">
        Week {lesson.weekNumber} · {lesson.moduleTitle}
      </p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight">{lesson.title}</h1>
      {lesson.estMinutes > 0 && (
        <p className="mt-1 text-xs text-muted">~{lesson.estMinutes} min</p>
      )}

      <div className="prose-constare mt-8 whitespace-pre-wrap text-text">
        {lesson.body || (
          <em className="text-muted">Lesson content is being authored.</em>
        )}
      </div>

      <div className="mt-10 flex items-center gap-4 border-t border-line pt-6">
        {completedAt ? (
          <span className="text-sm text-amber">
            ✓ Completed {new Date(completedAt).toLocaleDateString()}
          </span>
        ) : (
          <button
            onClick={markComplete}
            className="rounded-md bg-amber px-4 py-2 text-sm font-semibold text-ink hover:bg-amber-hover"
          >
            Mark complete
          </button>
        )}
      </div>
    </article>
  );
}
